import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { AdminLayout } from "@/app/routes/admin-layout";
import { LoginPage } from "@/app/routes/admin-login";
import { AppSettingsProvider, useAppSettings } from "@/components/app-settings/app-settings-provider";
import { AuthProvider } from "@/components/auth/auth-provider";
import { ProtectedAdminRoute } from "@/components/auth/protected-admin-route";
import { workbenchRoutePatterns, workbenchRoutes } from "@/lib/routes/workbench-routes";
import "@/app/styles/global.css";

const DashboardPage = React.lazy(() => import("@/app/routes/admin-dashboard").then((module) => ({ default: module.DashboardPage })));
const QuotationsPage = React.lazy(() => import("@/app/routes/admin-quotations").then((module) => ({ default: module.QuotationsPage })));
const QuoteNewPage = React.lazy(() => import("@/app/routes/admin-quote-new").then((module) => ({ default: module.QuoteNewPage })));
const QuoteEditPage = React.lazy(() => import("@/app/routes/admin-quote-edit").then((module) => ({ default: module.QuoteEditPage })));
const QuotePreviewPage = React.lazy(() => import("@/app/routes/admin-quote-preview").then((module) => ({ default: module.QuotePreviewPage })));
const SettingsPage = React.lazy(() => import("@/app/routes/admin-settings").then((module) => ({ default: module.SettingsPage })));
const PublicQuotePage = React.lazy(() => import("@/app/routes/public-quote").then((module) => ({ default: module.PublicQuotePage })));

function LazyRoute({ children }: { children: React.ReactNode }) {
  const { t } = useAppSettings();

  return (
    <React.Suspense fallback={
      <div className="mx-auto min-h-64 w-full max-w-7xl px-4 py-8" role="status" aria-live="polite">
        <p className="text-sm text-muted-foreground">{t("dashboard.loading")}</p>
        <div className="mt-5 space-y-3" aria-hidden="true">
          <div className="h-6 w-40 rounded bg-muted" />
          <div className="h-10 w-full rounded bg-muted" />
          <div className="h-10 w-full rounded bg-muted" />
        </div>
      </div>
    }>
      {children}
    </React.Suspense>
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <AppSettingsProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Navigate to={workbenchRoutes.dashboard} replace />} />
            <Route path={workbenchRoutePatterns.login} element={<LoginPage />} />
            <Route
              element={
                <ProtectedAdminRoute>
                  <AdminLayout />
                </ProtectedAdminRoute>
              }
            >
              <Route path={workbenchRoutePatterns.dashboard} element={<LazyRoute><DashboardPage /></LazyRoute>} />
              <Route path={workbenchRoutePatterns.quotations} element={<LazyRoute><QuotationsPage /></LazyRoute>} />
              <Route path={workbenchRoutePatterns.newQuotation} element={<LazyRoute><QuoteNewPage /></LazyRoute>} />
              <Route path={workbenchRoutePatterns.quotationEdit} element={<LazyRoute><QuoteEditPage /></LazyRoute>} />
              <Route path={workbenchRoutePatterns.quotationPreview} element={<LazyRoute><QuotePreviewPage /></LazyRoute>} />
              <Route path={workbenchRoutePatterns.settings} element={<LazyRoute><SettingsPage /></LazyRoute>} />
              <Route path={workbenchRoutePatterns.settingsSection} element={<LazyRoute><SettingsPage /></LazyRoute>} />
            </Route>
            <Route path="/q/:token" element={<LazyRoute><PublicQuotePage /></LazyRoute>} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </AppSettingsProvider>
  </React.StrictMode>,
);
